// src/pages/operations-dashboard/components/ZoneWiseSection.jsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { MapPin } from "lucide-react";
import { formatCompact, formatPercent, CHART_COLORS } from "../../../utils/dashboardConstants";

const ZoneTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-sm font-semibold text-gray-800 mb-1">{d?.zone_name || d?.name}</p>
      <p className="text-lg font-bold text-gray-900">{formatCompact(d?.count || d?.value)}</p>
      {d?.percentage !== undefined && <p className="text-xs text-gray-400">{formatPercent(d.percentage)} of total</p>}
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function ZoneWiseSection({ data, loading }) {
  const isLoading = loading?.shipmentsZoneWise;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (!data) return null;

  const zones = (data.zone_counts || [])
    .map((z, i) => ({
      name: z.zone_name || z.zone_id || `Zone ${i + 1}`,
      value: z.count || 0,
      percentage: z.percentage || 0,
      zone_name: z.zone_name || z.zone_id,
      count: z.count || 0,
    }))
    .sort((a, b) => b.value - a.value);

  if (zones.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-indigo-50">
            <MapPin className="w-4.5 h-4.5 text-indigo-500" strokeWidth={2} />
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Zone-wise Distribution</h3>
        </div>
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
          No zone data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-indigo-50">
            <MapPin className="w-4.5 h-4.5 text-indigo-500" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Zone-wise Shipment Distribution</h3>
            <p className="text-xs text-gray-400 mt-0.5">Shipments split by delivery zones</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Bar Chart */}
          <div className="xl:col-span-2">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={zones} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCompact}
                />
                <Tooltip content={<ZoneTooltip />} cursor={{ fill: "#f8fafc", radius: 6 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={50}>
                  {zones.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Zone List */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">By Volume</p>
            {zones.map((z, i) => (
              <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{z.name}</p>
                  <div className="w-full h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(z.percentage, 100)}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold text-gray-800">{formatCompact(z.value)}</p>
                  <p className="text-[10px] text-gray-400">{formatPercent(z.percentage)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
